import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, Label, LoadingButton, StatusDot, Amount } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { getPublicAcceptance, respondToMilestone, respondToChangeOrder, PublicAcceptance, PublicMilestone, PublicChangeOrder, ChangeOrderDecision } from '../services/api';
import { AcceptanceStatus } from '../types/pm';
import { milestoneTone, projectTone, changeOrderTone, toneOf } from '../utils/statusTone';

const NAME_KEY = 'pm-acceptance-name';
const loadName = () => {
  try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; }
};
const saveName = (name: string) => {
  try { localStorage.setItem(NAME_KEY, name); } catch { /* 無痕模式等不可用時略過 */ }
};

/** 回覆表單（里程碑驗收、變更單核准共用）：姓名必填；說明視動作選填或必填 */
const ResponseForm: React.FC<{
  noteRequired: boolean;
  noteLabel: string;
  submitLabel: string;
  onSubmit: (name: string, note: string) => Promise<void>;
  onCancel: () => void;
}> = ({ noteRequired, noteLabel, submitLabel, onSubmit, onCancel }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const [name, setName] = useState(loadName);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const canSubmit = name.trim() && (!noteRequired || note.trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    try {
      saveName(name.trim());
      await onSubmit(name.trim(), note.trim());
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 space-y-3 rounded-md border border-line p-4">
      <div>
        <Label htmlFor="acceptance-name">{t('pmAcceptance.form.name', { ns: 'pages' })}<span className="text-fg ml-1">*</span></Label>
        <Input id="acceptance-name" value={name} onChange={e => setName(e.target.value)} maxLength={100} autoFocus={!name} />
      </div>
      <div>
        <Label htmlFor="acceptance-note">
          {noteLabel}
          {noteRequired && <span className="text-fg ml-1">*</span>}
        </Label>
        <Textarea id="acceptance-note" value={note} onChange={e => setNote(e.target.value)} maxLength={1000} rows={3} autoFocus={!!name} />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="inline-flex h-10 items-center rounded-md px-4 text-sm text-fg-secondary hover:text-fg">
          {t('common.actions.cancel')}
        </button>
        <LoadingButton type="submit" loading={sending} disabled={!canSubmit}>
          {submitLabel}
        </LoadingButton>
      </div>
    </form>
  );
};

/**
 * 客戶驗收頁（免登入，網址 /acceptance/:token）
 * 客戶看到里程碑進度；「已完成」的里程碑可以驗收通過或要求修改；待確認的變更單可以核准或拒絕，回覆都會通知專案負責人
 * 不顯示金額與內部任務；連結停用或重新產生後顯示「連結已失效」
 */
const AcceptancePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const [data, setData] = useState<PublicAcceptance | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [responding, setResponding] = useState<{ id: string; decision: AcceptanceStatus } | null>(null);
  const [deciding, setDeciding] = useState<{ id: string; decision: ChangeOrderDecision } | null>(null);

  useEffect(() => {
    getPublicAcceptance(token)
      .then(({ data }) => setData(data))
      .catch(() => setInvalid(true));
  }, [token]);

  const date = (value: string | null) => (value ? formatTableDate(value, i18n.language) : '—');

  const respond = async (milestone: PublicMilestone, decision: AcceptanceStatus, name: string, note: string) => {
    try {
      const { data: result } = await respondToMilestone(token, milestone.id, { decision, name, note: note || undefined });
      setData(prev => prev && { ...prev, milestones: prev.milestones.map(m => (m.id === milestone.id ? { ...m, ...result } : m)) });
      setResponding(null);
      toast.success(t(decision === 'ACCEPTED' ? 'pmAcceptance.acceptedToast' : 'pmAcceptance.changesToast', { ns: 'pages' }));
    } catch (error: any) {
      toast.error(error.response?.status === 429 ? t('pmAcceptance.tooMany', { ns: 'pages' }) : t('common.messages.operationFailed'));
    }
  };

  const decide = async (order: PublicChangeOrder, decision: ChangeOrderDecision, name: string, note: string) => {
    try {
      const { data: result } = await respondToChangeOrder(token, order.id, { decision, name, note: note || undefined });
      setData(prev => prev && { ...prev, changeOrders: prev.changeOrders.map(o => (o.id === order.id ? { ...o, ...result } : o)) });
      setDeciding(null);
      toast.success(t(decision === 'APPROVED' ? 'pmAcceptance.changeOrders.approvedToast' : 'pmAcceptance.changeOrders.rejectedToast', { ns: 'pages' }));
    } catch (error: any) {
      toast.error(error.response?.status === 429 ? t('pmAcceptance.tooMany', { ns: 'pages' }) : t('common.messages.operationFailed'));
    }
  };

  if (invalid) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-fg">{t('pmAcceptance.invalidTitle', { ns: 'pages' })}</h1>
          <p className="mt-2 text-sm text-fg-secondary">{t('pmAcceptance.invalidHint', { ns: 'pages' })}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  const { project, milestones, changeOrders } = data;
  const completed = milestones.filter(m => m.status === 'COMPLETED').length;
  const accepted = milestones.filter(m => m.acceptanceStatus === 'ACCEPTED').length;
  const percent = milestones.length ? Math.round((completed / milestones.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-page">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
        <p className="text-[13px] text-fg-secondary">{t('pmAcceptance.eyebrow', { ns: 'pages' })}</p>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold text-fg break-words">{project.name}</h1>
          <StatusDot tone={toneOf(projectTone, project.status)} label={t(`pmProjects.status.${project.status}`, { ns: 'pages' })} />
        </div>

        <dl className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-line bg-surface p-4">
            <dt className="text-sm text-fg-secondary">{t('pmAcceptance.progress', { ns: 'pages' })}</dt>
            <dd className="mt-1 text-2xl font-semibold text-fg tabular-nums">{percent}%</dd>
            <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div className="h-full bg-accent-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
          <div className="rounded-lg border border-line bg-surface p-4">
            <dt className="text-sm text-fg-secondary">{t('pmAcceptance.acceptedCount', { ns: 'pages' })}</dt>
            <dd className="mt-1 text-2xl font-semibold text-fg tabular-nums">{accepted} / {milestones.length}</dd>
          </div>
          <div className="rounded-lg border border-line bg-surface p-4">
            <dt className="text-sm text-fg-secondary">{t('pmProjects.fields.expectedDeliveryDate', { ns: 'pages' })}</dt>
            <dd className="mt-1 text-2xl font-semibold text-fg tabular-nums">{date(project.expectedDeliveryDate)}</dd>
          </div>
        </dl>

        <h2 className="mt-8 mb-3 text-base font-semibold text-fg">{t('pmAcceptance.milestones', { ns: 'pages' })}</h2>
        {milestones.length === 0 ? (
          <p className="rounded-lg border border-line bg-surface px-5 py-10 text-center text-sm text-fg-secondary">{t('pmAcceptance.noMilestones', { ns: 'pages' })}</p>
        ) : (
          <ol className="rounded-lg border border-line bg-surface divide-y divide-line">
            {milestones.map(milestone => {
              const canRespond = milestone.status === 'COMPLETED' && milestone.acceptanceStatus !== 'ACCEPTED';
              const open = responding?.id === milestone.id;
              return (
                <li key={milestone.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-medium text-fg break-words">{milestone.name}</span>
                        <StatusDot tone={toneOf(milestoneTone, milestone.status)} label={t(`pmMilestones.status.${milestone.status}`, { ns: 'pages' })} />
                      </div>
                      {milestone.description && <p className="mt-1 text-sm text-fg-secondary whitespace-pre-wrap break-words">{milestone.description}</p>}
                      <p className="mt-1 text-[13px] text-fg-secondary tabular-nums">
                        {milestone.status === 'COMPLETED'
                          ? t('pmAcceptance.completedOn', { ns: 'pages', date: date(milestone.completedDate) })
                          : t('pmAcceptance.dueOn', { ns: 'pages', date: date(milestone.dueDate) })}
                      </p>
                    </div>
                    {canRespond && !open && (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setResponding({ id: milestone.id, decision: 'CHANGES_REQUESTED' })}
                          className="inline-flex h-10 items-center rounded-md border border-line px-4 text-sm text-fg hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          {t('pmAcceptance.requestChanges', { ns: 'pages' })}
                        </button>
                        <button
                          type="button"
                          onClick={() => setResponding({ id: milestone.id, decision: 'ACCEPTED' })}
                          className="inline-flex h-10 items-center rounded-md bg-accent-fill px-4 text-sm font-medium text-white hover:opacity-90"
                        >
                          {t('pmAcceptance.accept', { ns: 'pages' })}
                        </button>
                      </div>
                    )}
                  </div>

                  {milestone.acceptanceStatus && (
                    <p className={`mt-2 text-[13px] ${milestone.acceptanceStatus === 'ACCEPTED' ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                      {t(`pmAcceptance.result.${milestone.acceptanceStatus}`, { ns: 'pages', name: milestone.acceptanceBy, date: date(milestone.acceptanceAt) })}
                      {milestone.acceptanceNote && <span className="block mt-0.5 text-fg-secondary whitespace-pre-wrap break-words">{milestone.acceptanceNote}</span>}
                    </p>
                  )}

                  {open && (
                    <ResponseForm
                      noteRequired={responding.decision === 'CHANGES_REQUESTED'}
                      noteLabel={t(responding.decision === 'CHANGES_REQUESTED' ? 'pmAcceptance.form.changes' : 'pmAcceptance.form.note', { ns: 'pages' })}
                      submitLabel={t(responding.decision === 'CHANGES_REQUESTED' ? 'pmAcceptance.requestChanges' : 'pmAcceptance.accept', { ns: 'pages' })}
                      onCancel={() => setResponding(null)}
                      onSubmit={(name, note) => respond(milestone, responding.decision, name, note)}
                    />
                  )}
                </li>
              );
            })}
          </ol>
        )}
        {changeOrders.length > 0 && (
          <>
            <h2 className="mt-8 mb-1 text-base font-semibold text-fg">{t('pmAcceptance.changeOrders.title', { ns: 'pages' })}</h2>
            <p className="mb-3 text-[13px] text-fg-secondary">{t('pmAcceptance.changeOrders.hint', { ns: 'pages' })}</p>
            <ul className="rounded-lg border border-line bg-surface divide-y divide-line">
              {changeOrders.map(order => {
                const open = deciding?.id === order.id;
                return (
                  <li key={order.id} className="px-5 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="font-medium text-fg break-words">{order.title}</span>
                          <StatusDot tone={toneOf(changeOrderTone, order.status)} label={t(`pmChangeOrders.status.${order.status}`, { ns: 'pages' })} />
                        </div>
                        {order.description && <p className="mt-1 text-sm text-fg-secondary whitespace-pre-wrap break-words">{order.description}</p>}
                        <p className="mt-1 text-sm text-fg tabular-nums">
                          <Amount value={order.amount} />
                          {order.estimatedHours ? <span className="ml-2 text-[13px] text-fg-secondary">{t('pmAcceptance.changeOrders.hours', { ns: 'pages', hours: order.estimatedHours })}</span> : null}
                        </p>
                      </div>
                      {order.status === 'PENDING' && !open && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setDeciding({ id: order.id, decision: 'REJECTED' })}
                            className="inline-flex h-10 items-center rounded-md border border-line px-4 text-sm text-fg hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            {t('pmAcceptance.changeOrders.reject', { ns: 'pages' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeciding({ id: order.id, decision: 'APPROVED' })}
                            className="inline-flex h-10 items-center rounded-md bg-accent-fill px-4 text-sm font-medium text-white hover:opacity-90"
                          >
                            {t('pmAcceptance.changeOrders.approve', { ns: 'pages' })}
                          </button>
                        </div>
                      )}
                    </div>
                    {order.decidedAt && (
                      <p className={`mt-2 text-[13px] ${order.status === 'APPROVED' ? 'text-green-700 dark:text-green-400' : 'text-fg-secondary'}`}>
                        {t(`pmAcceptance.changeOrders.${order.decidedBy ? 'result' : 'resultByTeam'}.${order.status}`, { ns: 'pages', name: order.decidedBy, date: date(order.decidedAt) })}
                        {order.decisionNote && <span className="block mt-0.5 text-fg-secondary whitespace-pre-wrap break-words">{order.decisionNote}</span>}
                      </p>
                    )}
                    {open && (
                      <ResponseForm
                        noteRequired={false}
                        noteLabel={t('pmAcceptance.form.note', { ns: 'pages' })}
                        submitLabel={t(deciding.decision === 'APPROVED' ? 'pmAcceptance.changeOrders.approve' : 'pmAcceptance.changeOrders.reject', { ns: 'pages' })}
                        onCancel={() => setDeciding(null)}
                        onSubmit={(name, note) => decide(order, deciding.decision, name, note)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <p className="mt-6 text-center text-[13px] text-fg-muted">RedPanda PM</p>
      </div>
    </div>
  );
};

export default AcceptancePage;
